/**
 * @file ConsentKindItem.tsx
 * @description Один вид согласия в «Согласиях» клиента (phase19.16, партия
 * «Юридические документы», шаг Б; uiux-legal-steps-b-c §3.1): статус,
 * редакция документа со ссылкой ↗ на сайт или текст «Редакция не
 * записана…», когда и где принято, IP; «История (N)» при N ≥ 2 — все
 * записи вида, новые сверху, только чтение; «Отозвать» — у записей, которые
 * отзывает магазин (записи из ЛК отзывает только клиент).
 */

import { ChevronDown, ExternalLink } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../../components/ui/button";
import { StatusPill } from "../../../../components/ui/StatusPill";
import { siteHref } from "../../../../hooks/useShopSiteUrl";
import type { ConsentKindGroupDto, ConsentRecordDto } from "../../../../lib/api-customers";
import { CONSENT_KIND_LABEL, consentNoRevisionText, consentRevisionText, consentSourceText } from "../../../../lib/customer-consents";
import { formatShopDateTime } from "../../../../lib/shop-time";

interface Props {
  group: ConsentKindGroupDto;
  siteUrl: string | null;
  canRevoke: boolean;
  onRevoke: (record: ConsentRecordDto) => void;
}

/** Редакция документа: ссылка на сайт или пояснение, почему её нет. */
function RevisionLine({ record, siteUrl }: { record: ConsentRecordDto; siteUrl: string | null }) {
  const rev = record.legalRevision;
  if (!rev) {
    const text = consentNoRevisionText(record);
    return text ? <span className="type-caption text-on-surface-variant">{text}</span> : null;
  }
  const href = siteHref(siteUrl, rev.href);
  const text = consentRevisionText(rev);
  if (!href) return <span className="type-body break-words text-on-surface">{text}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="type-body inline-flex min-h-12 items-center gap-2 break-words text-link hover:underline">
      <span>{text}</span>
      <ExternalLink aria-hidden className="size-4 shrink-0" />
      <span className="sr-only"> (откроется на сайте в новой вкладке)</span>
    </a>
  );
}

/** Строка истории: «19.10.2026, 14:05 · принято · ред. 2 · при оформлении заказа · заказ Z-…». */
function HistoryEntry({ record }: { record: ConsentRecordDto }) {
  const rev = record.legalRevision ? `ред. ${record.legalRevision.number}` : null;
  const order = record.order ? `заказ ${record.order.number}` : null;
  const accepted = [formatShopDateTime(record.acceptedAt), "принято", rev, consentSourceText(record), order].filter(Boolean).join(" · ");
  return (
    <li className="type-caption flex flex-col gap-0.5 py-1 text-on-surface-variant">
      <span>{accepted}</span>
      {record.revokedAt ? (
        <span>{[formatShopDateTime(record.revokedAt), "отозвано", record.revokedByName ?? null].filter(Boolean).join(" · ")}</span>
      ) : null}
    </li>
  );
}

export function ConsentKindItem({ group, siteUrl, canRevoke, onRevoke }: Props) {
  const [open, setOpen] = useState(false);
  const c = group.current;
  const meta = [
    `Принято ${formatShopDateTime(c.acceptedAt)} · ${consentSourceText(c)}`,
    c.revokedAt ? `Отозвано ${formatShopDateTime(c.revokedAt)}` : null,
    c.ipAddress ? `IP ${c.ipAddress}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const active = group.active;
  const revocable = canRevoke && active && c.origin !== "PORTAL";
  const count = group.history.length;

  return (
    <div className="flex flex-col gap-1 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="type-body font-medium text-on-surface">{CONSENT_KIND_LABEL[group.kind]}</span>
        <StatusPill tone={active ? "success" : "neutral"}>{active ? "Действует" : "Отозвано"}</StatusPill>
        {c.origin === "PORTAL" ? <StatusPill tone="neutral">ЛК</StatusPill> : null}
      </div>
      <RevisionLine record={c} siteUrl={siteUrl} />
      <span className="type-caption break-words text-on-surface-variant">{meta}</span>
      {count >= 2 || revocable ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {count >= 2 ? (
            <Button variant="ghost" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
              История ({count})
              <ChevronDown aria-hidden className={open ? "size-4 rotate-180" : "size-4"} />
            </Button>
          ) : (
            <span />
          )}
          {revocable ? (
            <Button variant="ghost" className="text-warning hover:bg-warning/10" onClick={() => onRevoke(c)}>
              Отозвать
            </Button>
          ) : null}
        </div>
      ) : null}
      {open ? (
        <ul aria-label={`История: ${CONSENT_KIND_LABEL[group.kind]}`} className="border-l-2 border-[var(--border)] pl-3">
          {group.history.map((record) => (
            <HistoryEntry key={record.id} record={record} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
